import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Zap, X } from "lucide-react";
import { fetchLaunchStatus } from "@/lib/api";

const DISMISS_KEY = "rigcalc.launch_banner.dismissed_until";

function fmtBox(n, label) {
  return (
    <div className="flex flex-col items-center leading-none">
      <span className="mono text-sm md:text-base font-bold text-[#0b0e13] tabular-nums">
        {String(n).padStart(2, "0")}
      </span>
      <span className="mono text-[8px] tracking-widest text-[#0b0e13]/70 uppercase">{label}</span>
    </div>
  );
}

export default function LaunchBanner() {
  const [status, setStatus] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [hidden, setHidden] = useState(true);

  // Check dismissal (24h snooze)
  useEffect(() => {
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    if (Date.now() < until) return;
    setHidden(false);
    fetchLaunchStatus().then(setStatus).catch(() => {});
  }, []);

  // Ticker
  useEffect(() => {
    if (!status?.active) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [status?.active]);

  const dismiss = () => {
    // Snooze 24 hours
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
    setHidden(true);
  };

  if (hidden || !status?.active || !status?.end_date) return null;

  const diff = new Date(status.end_date).getTime() - now;
  if (diff <= 0) return null;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);

  return (
    <div
      data-testid="launch-countdown-banner"
      className="relative bg-gradient-to-r from-[#f59e0b] via-[#fbbf24] to-[#f59e0b] text-[#0b0e13] px-3 md:px-6 py-2 flex items-center justify-center gap-3 md:gap-5 border-b-2 border-[#0b0e13]/20"
    >
      <Zap className="w-4 h-4 shrink-0" strokeWidth={2.5} />
      <div className="flex items-center gap-2 md:gap-3 flex-wrap justify-center text-center">
        <span className="mono text-[10px] md:text-xs tracking-widest font-bold uppercase">
          Launch Offer · Lifetime
        </span>
        <span className="mono text-xs md:text-sm font-bold">
          ${status.lifetime_launch_price}
          <span className="line-through opacity-60 ml-1.5 font-normal">
            ${status.lifetime_regular_price}
          </span>
        </span>
        <span className="hidden sm:inline text-[#0b0e13]/60">·</span>
        <div className="flex items-center gap-1.5" data-testid="launch-countdown-ticker">
          {fmtBox(d, "D")}
          <span className="mono font-bold">:</span>
          {fmtBox(h, "H")}
          <span className="mono font-bold">:</span>
          {fmtBox(m, "M")}
          <span className="mono font-bold">:</span>
          {fmtBox(s, "S")}
        </div>
        <Link
          to="/pricing"
          data-testid="launch-banner-cta"
          className="mono text-[10px] md:text-xs tracking-widest font-bold uppercase bg-[#0b0e13] text-[#f59e0b] px-3 py-1.5 hover:bg-[#11151c] transition-colors ml-1"
        >
          Lock In Deal →
        </Link>
      </div>
      <button
        data-testid="launch-banner-dismiss"
        onClick={dismiss}
        aria-label="Dismiss launch banner"
        className="absolute right-2 md:right-3 top-1/2 -translate-y-1/2 text-[#0b0e13]/70 hover:text-[#0b0e13] transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
